import React from 'react';
import { Input } from './input';
import { Label } from './label';

interface PhoneInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: string;
    label?: string;
    id?: string;
    country?: 'RU' | 'KG';
}

// Функция форматирования телефона
const formatPhoneNumber = (value: string, country: 'RU' | 'KG' = 'RU'): string => {
    // Удаляем все символы кроме цифр
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';

    if (country === 'KG') {
        const localDigits = digits.startsWith('996') ? digits.slice(3) : digits;
        const limitedDigits = localDigits.slice(0, 9);
        if (!limitedDigits) return '+996';
        if (limitedDigits.length <= 3) return `+996 (${limitedDigits}`;
        if (limitedDigits.length <= 6) return `+996 (${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
        return `+996 (${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }

    let normalizedDigits = digits;
    if (digits.length <= 10 && !digits.startsWith('7') && !digits.startsWith('8')) {
        normalizedDigits = `7${digits}`;
    } else if (digits.startsWith('8')) {
        normalizedDigits = `7${digits.slice(1)}`;
    }

    // Ограничиваем до 11 цифр (7 + 10)
    const limitedDigits = normalizedDigits.slice(0, 11);

    // Форматируем в зависимости от количества цифр
    if (limitedDigits.length === 1) return '+7';
    if (limitedDigits.length <= 4) return `+7 (${limitedDigits.slice(1)}`;
    if (limitedDigits.length <= 7) return `+7 (${limitedDigits.slice(1, 4)}) ${limitedDigits.slice(4)}`;
    if (limitedDigits.length <= 9) return `+7 (${limitedDigits.slice(1, 4)}) ${limitedDigits.slice(4, 7)}-${limitedDigits.slice(7)}`;
    return `+7 (${limitedDigits.slice(1, 4)}) ${limitedDigits.slice(4, 7)}-${limitedDigits.slice(7, 9)}-${limitedDigits.slice(9)}`;
};

export const PhoneInput: React.FC<PhoneInputProps> = ({
    value,
    onChange,
    placeholder = "+7 (999) 123-45-67",
    disabled = false,
    error,
    label,
    id,
    country = 'RU',
}) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const formattedValue = formatPhoneNumber(inputValue, country);
        onChange(formattedValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Разрешаем навигационные клавиши
        if ([
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End'
        ].includes(e.key)) {
            return;
        }

        // Разрешаем Ctrl/Cmd-сочетания независимо от раскладки клавиатуры.
        if ((e.ctrlKey || e.metaKey) && (
            ['KeyA', 'KeyC', 'KeyV', 'KeyX', 'KeyZ', 'KeyY'].includes(e.code) ||
            ['a', 'c', 'v', 'x', 'z', 'y'].includes(e.key.toLowerCase())
        )) {
            return;
        }

        // Разрешаем только цифры
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const formattedValue = formatPhoneNumber(pastedText, country);
        onChange(formattedValue);
    };

    return (
        <div className="space-y-2">
            {label && <Label htmlFor={id}>{label}</Label>}
            <Input
                id={id}
                type="tel"
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={country === 'KG' ? 19 : 18}
            />
            {error && (
                <p className="text-sm text-red-500">{error}</p>
            )}
        </div>
    );
};
